import { isEqual } from 'lodash';
import { guards } from '@shared/helpers/typeguards.helpers';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import type { BuildingDesign } from '@shared/models/design/building-design.model';
import { omitBlueprintRasterFromUnderlay } from '@shared/helpers/design/blueprint-underlay-transfer.helpers';
import {
  getNormalizedBlueprintUnderlays,
  withBlueprintUnderlays,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import {
  fetchBlueprintUnderlayImage,
  uploadBlueprintUnderlayImage,
  blobToDataUrl,
} from '../api/blueprint-underlay-image.api';
import {
  forgetBlueprintRaster,
  rememberBlueprintRaster,
  resolveBlueprintRaster,
} from './blueprint-underlay-raster.cache';

/** Shown when a snapshot persist discarded the underlay rows. */
export const BLUEPRINT_UNDERLAY_PERSIST_FAILED =
  'Blueprint underlay could not be saved. Please try again.';

type PersistBuildingDesign = (
  design: BuildingDesign,
  options?: { skipEnrichment?: boolean; skipDesignPersistLock?: boolean },
) => Promise<unknown>;

type PersistBlueprintUnderlayMetadataDeps = {
  getDesign: () => BuildingDesign;
  updateDesign: PersistBuildingDesign;
};

/**
 * Patch applied to the live underlay list after the persist lock.
 */
export type BlueprintUnderlaysPatcher = (
  live: readonly BlueprintUnderlay[],
) => readonly BlueprintUnderlay[];

/**
 * Next underlay list, or a patch applied to the live list after the persist lock.
 */
export type BlueprintUnderlaysPersistInput =
  readonly BlueprintUnderlay[] | BlueprintUnderlaysPatcher;

/**
 * True when persist input is a live-list patcher rather than an absolute list.
 */
export const isBlueprintUnderlaysPatch = (
  input: BlueprintUnderlaysPersistInput,
): input is BlueprintUnderlaysPatcher => guards.is.function(input);

/**
 * Resolve {@link BlueprintUnderlaysPersistInput} against the live underlay list.
 * Patchers run after the design persist lock so pages that landed while waiting
 * are not dropped.
 */
export const resolveBlueprintUnderlaysPersistInput = (
  live: readonly BlueprintUnderlay[],
  input: BlueprintUnderlaysPersistInput,
): readonly BlueprintUnderlay[] => {
  if (isBlueprintUnderlaysPatch(input)) return input(live);
  return input;
};

/**
 * True when an underlay still has a raster that has not been uploaded.
 */
export const hasRasterAwaitingUpload = (underlay: BlueprintUnderlay): boolean =>
  Boolean(underlay.imageDataUrl && !underlay.imageRef);

/**
 * True when every underlay that still needs a raster PUT exists on the
 * persisted design. False means the metadata save did not land — uploading
 * then 404s on a missing row.
 */
export const areRastersAwaitingUploadPersisted = (
  intended: readonly BlueprintUnderlay[],
  persisted: readonly BlueprintUnderlay[],
): boolean => {
  const persistedIds = new Set(persisted.map((underlay) => underlay.id));
  return intended
    .filter(hasRasterAwaitingUpload)
    .every((underlay) => persistedIds.has(underlay.id));
};

/**
 * True when the live design contains the intended underlay rows, pose, and
 * image refs. Used after a snapshot persist that may have been 422-reset.
 */
export const didBlueprintUnderlaysPersist = (
  intended: readonly BlueprintUnderlay[],
  live: readonly BlueprintUnderlay[],
): boolean => {
  if (intended.length !== live.length) return false;
  const liveById = new Map(live.map((underlay) => [underlay.id, underlay]));
  return intended.every((underlay) => {
    const found = liveById.get(underlay.id);
    if (!found) return false;
    if (underlay.imageRef && found.imageRef !== underlay.imageRef) return false;
    return isEqual(
      persistMergeSignature(underlay),
      persistMergeSignature(found),
    );
  });
};

/**
 * Save underlay rows before JPEG PUT. Throws when the snapshot did not land
 * so callers skip the image upload (it 404s on a missing row).
 */
export const persistBlueprintUnderlayMetadata = async (
  underlays: readonly BlueprintUnderlay[],
  deps: PersistBlueprintUnderlayMetadataDeps,
): Promise<void> => {
  if (!underlays.some(hasRasterAwaitingUpload)) return;
  await deps.updateDesign(
    withBlueprintUnderlays(
      deps.getDesign(),
      stripRastersAwaitingUpload(underlays),
    ),
    { skipEnrichment: true },
  );
  if (
    areRastersAwaitingUploadPersisted(
      underlays,
      getNormalizedBlueprintUnderlays(deps.getDesign()),
    )
  ) {
    return;
  }
  throw new Error(BLUEPRINT_UNDERLAY_PERSIST_FAILED);
};

/**
 * Metadata-only underlays so the first project save can land before PUT.
 */
export const stripRastersAwaitingUpload = (
  underlays: readonly BlueprintUnderlay[],
): BlueprintUnderlay[] =>
  underlays.map((underlay) => {
    if (!hasRasterAwaitingUpload(underlay)) {
      return omitBlueprintRasterFromUnderlay(underlay);
    }
    const { imageDataUrl: _raster, imageRef: _ref, ...rest } = underlay;
    return rest;
  });

/**
 * Upload a raster if needed and return the underlay without {@link BlueprintUnderlay.imageDataUrl}.
 */
export const prepareBlueprintUnderlayForPersist = async (
  projectId: number,
  underlay: BlueprintUnderlay,
): Promise<BlueprintUnderlay> => {
  if (underlay.imageDataUrl) {
    rememberBlueprintRaster(underlay.id, underlay.imageDataUrl);
  }

  if (underlay.imageRef) {
    return omitBlueprintRasterFromUnderlay(underlay);
  }

  if (!underlay.imageDataUrl) return underlay;

  const imageRef = await uploadBlueprintUnderlayImage(
    projectId,
    underlay.id,
    underlay.imageDataUrl,
  );
  return omitBlueprintRasterFromUnderlay({ ...underlay, imageRef });
};

/**
 * Upload every page. Blob deletes for removed pages happen on the server
 * after the project row is committed so a failed save cannot drop rasters.
 */
export const prepareBlueprintUnderlaysForPersist = async (
  projectId: number,
  previous: readonly BlueprintUnderlay[],
  next: readonly BlueprintUnderlay[],
): Promise<BlueprintUnderlay[]> => {
  const prepared = await Promise.all(
    next.map((underlay) =>
      prepareBlueprintUnderlayForPersist(projectId, underlay),
    ),
  );

  const nextIds = new Set(prepared.map((underlay) => underlay.id));
  previous
    .filter((underlay) => !nextIds.has(underlay.id))
    .forEach((underlay) => forgetBlueprintRaster(underlay.id));

  return prepared;
};

/**
 * Pose / visibility / scale fields for merge, ignoring rasters and blob keys.
 */
const persistMergeSignature = (underlay: BlueprintUnderlay) => {
  const { imageRef: _ref, ...rest } = omitBlueprintRasterFromUnderlay(underlay);
  return rest;
};

/**
 * After JPEG PUT, apply this persist's fields unless Design changed during PUT.
 *
 * {@link prepared} is the intended list for this persist. Ids missing from
 * {@link current} are dropped when they were in {@link previous} (deleted
 * during PUT). New ids that were never in {@link previous} are kept even if
 * the store has not been written yet. Live fields overlay {@link prepared}
 * only when {@link current} differs from {@link previous} for that id.
 */
export const mergePreparedBlueprintUnderlays = (
  current: readonly BlueprintUnderlay[],
  prepared: readonly BlueprintUnderlay[],
  previous: readonly BlueprintUnderlay[],
): BlueprintUnderlay[] => {
  const currentById = new Map(
    current.map((underlay) => [underlay.id, underlay]),
  );
  const previousById = new Map(
    previous.map((underlay) => [underlay.id, underlay]),
  );
  const previousIds = new Set(previousById.keys());
  const fromPrepared = prepared.flatMap((underlay) => {
    const live = currentById.get(underlay.id);
    // Gone from live and present at persist start → deleted while PUT was in flight.
    if (!live) {
      if (previousById.has(underlay.id)) return [];
      return [omitBlueprintRasterFromUnderlay(underlay)];
    }
    const prior = previousById.get(underlay.id);
    const liveUnchanged =
      prior !== undefined &&
      isEqual(persistMergeSignature(live), persistMergeSignature(prior));
    const base = liveUnchanged ? underlay : live;
    return [
      omitBlueprintRasterFromUnderlay({
        ...base,
        imageRef: underlay.imageRef ?? live.imageRef,
      }),
    ];
  });
  const concurrentAdds = current
    .filter(
      (underlay) =>
        !prepared.some((entry) => entry.id === underlay.id) &&
        !previousIds.has(underlay.id),
    )
    .map(omitBlueprintRasterFromUnderlay);
  return [...fromPrepared, ...concurrentAdds];
};

const hydrateInFlight = new Map<string, Promise<string | undefined>>();

/**
 * Whether GET should run. Local rasters (import cache / data URL) paint
 * without a fetch. Otherwise GET so reload can recover a blob even when
 * {@link BlueprintUnderlay.imageRef} is missing on the project row.
 */
export const shouldFetchBlueprintUnderlayImage = (
  underlay: Pick<BlueprintUnderlay, 'id' | 'imageDataUrl'>,
): boolean => !resolveBlueprintRaster(underlay.id, underlay.imageDataUrl);

/**
 * Fetch a missing raster into the cache (reload / another session).
 * In-flight GETs for the same project/underlay are shared. Uses
 * {@link shouldFetchBlueprintUnderlayImage} so import does not GET while a
 * local raster is already available.
 */
export const hydrateBlueprintRaster = async (
  projectId: number,
  underlay: BlueprintUnderlay,
): Promise<string | undefined> => {
  if (!shouldFetchBlueprintUnderlayImage(underlay)) {
    const cached = resolveBlueprintRaster(underlay.id, underlay.imageDataUrl);
    if (cached) rememberBlueprintRaster(underlay.id, cached);
    return cached;
  }

  const key = `${projectId}:${underlay.id}`;
  const pending = hydrateInFlight.get(key);
  if (pending) return pending;

  const request = (async (): Promise<string | undefined> => {
    try {
      const blob = await fetchBlueprintUnderlayImage(projectId, underlay.id);
      const dataUrl = await blobToDataUrl(blob);
      rememberBlueprintRaster(underlay.id, dataUrl);
      return dataUrl;
    } catch (error) {
      console.error('Failed to fetch blueprint underlay raster', {
        projectId,
        underlayId: underlay.id,
        error,
      });
      return undefined;
    } finally {
      hydrateInFlight.delete(key);
    }
  })();
  hydrateInFlight.set(key, request);
  return request;
};
